import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag, scrollToId } from "./Reveal";

const STEPS = [
  {
    n: "01",
    title: "Tell Us What You Need",
    text: "Product, quantity, location and delivery requirements — one simple request.",
    chips: ["Product", "Quantity", "Location", "Required date"],
  },
  {
    n: "02",
    title: "Ajemi Sources",
    text: "We identify suitable supply options through our qualified supply network.",
  },
  {
    n: "03",
    title: "Suppliers Respond",
    text: "Selected supply partners provide commercial offers against your requirement.",
  },
  {
    n: "04",
    title: "Ajemi Evaluates",
    text: "We review the available options and coordinate the procurement — you get one clear recommendation, not a chaotic supplier list.",
  },
  {
    n: "05",
    title: "Customer Approves",
    text: "You review and approve the recommended procurement arrangement. Nothing moves without your sign-off.",
  },
  {
    n: "06",
    title: "Supplier Fulfills",
    text: "The selected supplier fulfills the order according to the agreed terms.",
  },
  {
    n: "07",
    title: "Ajemi Continues Managing It",
    text: "For recurring customers, Ajemi keeps managing future procurement requirements — cycle after cycle.",
  },
];

export default function HowItWorks() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.55"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <SectionTag number="07" label="How It Works" />
              <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white">
                From request <span className="text-gold">to delivery.</span>
              </h2>
              <p className="mt-6 text-white/55 text-base md:text-lg leading-relaxed max-w-md">
                Seven clear steps. The complexity stays inside the platform — you always know
                where your procurement stands, and you approve every commercial decision.
              </p>
              <button
                data-testid="how-it-works-cta"
                onClick={() => scrollToId("quote")}
                className="mt-10 group inline-flex items-center gap-3 border border-gold/50 text-gold font-outfit rounded-full pl-7 pr-2.5 py-2.5 hover:bg-gold hover:text-ink transition-all duration-300"
              >
                Start a Request
                <span className="bg-gold/15 rounded-full p-2 group-hover:bg-ink/10 group-hover:rotate-45 transition-all duration-300">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7 relative" ref={ref}>
          <div className="absolute left-[7px] top-3 bottom-3 w-px bg-white/10" />
          <motion.div
            className="absolute left-[7px] top-3 bottom-3 w-px bg-gradient-to-b from-gold to-volt origin-top"
            style={{ scaleY }}
            data-testid="timeline-progress"
          />
          <div className="space-y-16">
            {STEPS.map((step) => (
              <Reveal key={step.n}>
                <div className="relative pl-12" data-testid={`timeline-step-${step.n}`}>
                  <motion.span
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5 }}
                    className="absolute left-0 top-2 w-[15px] h-[15px] rounded-full border-2 border-gold bg-ink shadow-[0_0_16px_rgba(217,164,65,0.4)]"
                  />
                  <span className="font-outfit text-5xl md:text-6xl font-extralight text-stroke">
                    {step.n}
                  </span>
                  <h3 className="font-outfit text-2xl md:text-3xl font-light text-white mt-3">
                    {step.title}
                  </h3>
                  <p className="text-white/55 mt-2 max-w-lg leading-relaxed">{step.text}</p>
                  {step.chips && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {step.chips.map((c) => (
                        <span
                          key={c}
                          className="text-[11px] tracking-[0.15em] uppercase text-white/50 border border-white/10 rounded-full px-3 py-1"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
